<template>
  <v-container class="about-page py-6" fluid>
    <!-- Hero -->
    <section class="about-hero">
      <div class="hero-content">
        <img
          class="hero-logo"
          :src="heroLogo"
          alt="EARNS Logo"
        />

        <div class="hero-copy">
          <v-chip
            color="primary"
            variant="tonal"
            size="small"
            class="mb-4"
          >
            <v-icon start size="18">
              mdi-lightning-bolt-outline
            </v-icon>

            Energy Efficiency Toolbox
          </v-chip>

          <h1 class="hero-title">
            Energieeffizienz einfach analysieren
          </h1>

          <p class="hero-description">
            EARNS unterstützt bei der schnellen, wissenschaftlich fundierten
            Bewertung technischer Energieeffizienzmaßnahmen in
            Bestandsgebäuden.
          </p>

          <div class="hero-actions">
            <v-btn
              color="primary"
              variant="elevated"
              :to="{ name: 'Register' }"
            >
              <v-icon start>mdi-account-plus-outline</v-icon>
              Jetzt registrieren
            </v-btn>

            <v-btn
              color="primary"
              variant="tonal"
              :to="{ name: 'Contact' }"
            >
              <v-icon start>mdi-email-outline</v-icon>
              Kontakt
            </v-btn>
          </div>
        </div>
      </div>
    </section>

    <!-- Main content -->
    <article class="about-content">
      <!-- About -->
      <section class="content-section">
        <div class="section-heading">
          <div class="section-heading-icon">
            <v-icon color="primary" size="24">
              mdi-information-outline
            </v-icon>
          </div>

          <div>
            <h2>Über EARNS</h2>
            <p>Energieanalysen effizient und nachvollziehbar durchführen</p>
          </div>
        </div>

        <div class="about-introduction">
          <div class="introduction-copy">
            <p>
              EARNS steht für
              <strong>Energy Analysis Expert NetworkS</strong> und ist ein
              Werkzeug für die Energieanalyse technischer Anlagen in
              Bestandsgebäuden.
            </p>

            <p>
              EARNS bietet eine effiziente Alternative zu üblichen
              Analyseverfahren und unterstützt bei der Entwicklung
              wirtschaftlicher und umsetzungsorientierter
              Effizienzmaßnahmen.
            </p>

            <p>
              Mit EARNS lassen sich potenzielle Einsparungen durch
              energieeffiziente Maßnahmen schnell berechnen und bewerten –
              beispielsweise durch die Umrüstung auf LED-Beleuchtung oder
              den Austausch von Ventilatoren.
            </p>
          </div>
          <div class="introduction-image">
            <img
              class="feature-image"
              :src="homeImg"
              alt="EARNS Home"
            />
          </div>
        </div>
      </section>

      <!-- Key features -->
      <section class="content-section">
        <div class="section-heading">
          <div class="section-heading-icon">
            <v-icon color="primary" size="24">
              mdi-star-four-points-outline
            </v-icon>
          </div>

          <div>
            <h2>Schlüsselfunktionen</h2>
            <p>Die wichtigsten Funktionen auf einen Blick</p>
          </div>
        </div>

        <div class="feature-grid">
          <v-card
            v-for="feature in features"
            :key="feature.title"
            class="feature-card"
            elevation="0"
          >
            <div class="feature-card-icon">
              <v-icon color="primary" size="26">
                {{ feature.icon }}
              </v-icon>
            </div>

            <div>
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.description }}</p>
            </div>
          </v-card>
        </div>
      </section>

      <!-- Benefits -->
      <section class="content-section">
        <div class="section-heading">
          <div class="section-heading-icon">
            <v-icon color="primary" size="24">
              mdi-chart-line
            </v-icon>
          </div>

          <div>
            <h2>Vorteile von Energieeffizienzanalysen mit EARNS</h2>
            <p>Fundierte Ergebnisse bei reduziertem Analyseaufwand</p>
          </div>
        </div>

        <div class="benefits-layout">
          <img
            class="industry-image"
            :src="industryImg"
            alt="Industry Building"
          />

          <div class="benefits-copy">
            <p>
              EARNS ist eine benutzerfreundliche Plattform, mit der
              potenzielle Energieeinsparungen verschiedener Anlagen und
              Systeme in Unternehmen berechnet und bewertet werden können.
            </p>

            <p>
              Die Berechnungen basieren auf wissenschaftlichen und
              zertifizierten Methoden für verschiedene
              Querschnittstechnologien. Dadurch wird die Sicherheit und
              Nachvollziehbarkeit der ermittelten Einsparpotenziale erhöht.
            </p>

            <p>
              Eine schnelle Analyse reduziert den erforderlichen Aufwand und
              ermöglicht kostengünstigere Bewertungen. Zusätzlich unterstützt
              EARNS die automatische Erstellung qualitativ hochwertiger
              Ergebnisberichte.
            </p>

            <p>
              Insgesamt bietet EARNS eine innovative Lösung für
              Energieeffizienzanalysen in Bestandsgebäuden. So lassen sich
              Zeit und Kosten sparen, Maßnahmen gezielter entwickeln und
              Ergebnisse strukturiert dokumentieren.
            </p>
          </div>
        </div>
      </section>

      <!-- Program overview -->
      <section class="content-section program-section">
        <div class="section-heading">
          <div class="section-heading-icon">
            <v-icon color="primary" size="24">
              mdi-monitor-dashboard
            </v-icon>
          </div>

          <div>
            <h2>Programmübersicht</h2>
            <p>Der typische Arbeitsablauf in EARNS</p>
          </div>
        </div>

        <v-carousel
          v-model="currentSlide"
          :cycle="carouselCycle"
          :height="carouselHeight"
          class="program-carousel"
          hide-delimiter-background
          show-arrows="hover"
          @mouseenter="carouselPaused = true"
          @mouseleave="carouselPaused = false"
        >
          <v-carousel-item
            v-for="(image, index) in resolvedImages"
            :key="image.src"
          >
            <div class="carousel-slide">
              <div class="carousel-progress">
                <span>Schritt {{ index + 1 }}</span>
                <span>{{ index + 1 }} / {{ resolvedImages.length }}</span>
              </div>

              <div class="carousel-image-container">
                <Viewer :options="viewerOptions" class="carousel-viewer">
                  <img
                    :src="image.url"
                    :alt="image.title"
                    class="carousel-image"
                  >
                </Viewer>
                <!-- <Viewer :options="viewerOptions">
                  image.url
                </Viewer> -->
              </div>

              <div class="carousel-caption">
                <div class="carousel-caption-title">{{ image.title }}</div>
                <p class="carousel-caption-text">{{ image.caption }}</p>
              </div>
            </div>
          </v-carousel-item>
        </v-carousel>
      </section>

      <!-- Learn more -->
      <section class="content-section">
        <div class="section-heading">
          <div class="section-heading-icon">
            <v-icon color="primary" size="24">
              mdi-arrow-right-circle-outline
            </v-icon>
          </div>

          <div>
            <h2>Mehr erfahren</h2>
            <p>Kontaktieren Sie uns oder erstellen Sie ein Benutzerkonto</p>
          </div>
        </div>

        <div class="cta-panel">
          <div>
            <h3>Bereit, Energie und Kosten zu sparen?</h3>

            <p>
              Registrieren Sie sich für EARNS oder kontaktieren Sie uns für
              weitere Informationen.
            </p>
          </div>

          <div class="cta-actions">
            <v-btn
              color="primary"
              variant="elevated"
              :to="{ name: 'Register' }"
            >
              <v-icon start>mdi-account-plus-outline</v-icon>
              Registrieren
            </v-btn>

            <v-btn
              color="primary"
              variant="tonal"
              :to="{ name: 'Contact' }"
            >
              <v-icon start>mdi-email-outline</v-icon>
              Kontakt aufnehmen
            </v-btn>
          </div>
        </div>
      </section>

      <!-- Development -->
      <section class="content-section">
        <div class="section-heading">
          <div class="section-heading-icon">
            <v-icon color="primary" size="24">
              mdi-hammer-wrench
            </v-icon>
          </div>

          <div>
            <h2>Gemeinsame Weiterentwicklung</h2>
            <p>Ihr Feedback hilft bei der Verbesserung von EARNS</p>
          </div>
        </div>

        <v-alert
          type="info"
          variant="tonal"
          border="start"
          class="development-alert"
        >
          <template #title>
            EARNS wird kontinuierlich weiterentwickelt
          </template>

          <p>
            EARNS wird als Webanwendung laufend ausgebaut. Daher kann der
            Funktionsumfang an einzelnen Stellen noch eingeschränkt sein und
            es können vereinzelt Fehler auftreten.
          </p>

          <p class="mt-3">
            Läuft etwas nicht wie erwartet? Dann senden Sie uns bitte Ihr
            Feedback an
            <a
              href="mailto:earns@rom-technik.de?subject=Feedback"
              class="feedback-link"
            >
              earns@rom-technik.de
            </a>.
          </p>

          <p class="mt-3 mb-0">
            Vielen Dank für Ihre Unterstützung.<br />
            Ihre Forschung und Entwicklung
          </p>
        </v-alert>
      </section>
    </article>
  </v-container>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import 'viewerjs/dist/viewer.css'
import { component as Viewer } from 'v-viewer'

const currentSlide = ref(0)
const carouselPaused = ref(false)

const carouselCycle = computed(() => {
  return !carouselPaused.value
})

const carouselHeight = computed(() => {
  return 'clamp(560px, 72vw, 760px)'
})

const heroLogo = new URL(
  '../assets/romtechnikearns.png',
  import.meta.url
).href

const homeImg = new URL(
  '../assets/home.png',
  import.meta.url
).href

const industryImg = new URL(
  '../assets/IndustryBuilding.png',
  import.meta.url
).href

/*
 * Vite resolves all example images during the build.
 * This is more reliable than constructing a dynamic
 * new URL from a variable filename.
 */
// `<string>`, weil `query: '?url'` mit `import: 'default'` den fertigen Pfad
// liefert. Ohne die Angabe hält TypeScript den Wert für `unknown` und `:src`
// bekäme keinen String.
const exampleImageModules = import.meta.glob<string>(
  '../assets/examples/*.{png,jpg,jpeg,webp}',
  {
    eager: true,
    query: '?url',
    import: 'default',
  }
)

const images = [
  {
    src: 'Page1.png',
    title: 'Registrierung',
    caption:
      'Nach der Registrierung muss das Benutzerkonto von einem Administrator freigeschaltet werden.',
  },
  {
    src: 'Page2.png',
    title: 'Anmeldung',
    caption:
      'Die Anmeldung ist erst nach der Freischaltung des Benutzerkontos möglich.',
  },
  {
    src: 'Page3.png',
    title: 'Startseite',
    caption:
      'Von der Startseite aus lässt sich die Projektliste öffnen oder ein neues Projekt erstellen.',
  },
  {
    src: 'Page3-1.png',
    title: 'Passwort ändern',
    caption:
      'Über das Konto-Menü oben rechts lässt sich das Passwort jederzeit ändern.',
  },
  {
    src: 'Page4.png',
    title: 'Projekte',
    caption:
      'Alle Projekte werden übersichtlich aufgelistet. Beim Öffnen oder Erstellen eines Projekts erweitert sich die Navigation.',
  },
  {
    src: 'Page5.png',
    title: 'Projektdetails',
    caption:
      'Hier werden die grundlegenden Projektinformationen sowie die historischen Energieverbrauchsdaten erfasst.',
  },
  {
    src: 'Page6.png',
    title: 'Maßnahmen',
    caption:
      'Maßnahmen und Gruppen lassen sich erstellen, löschen, duplizieren, verschieben, umbenennen und von der Analyse ausschließen.',
  },
  {
    src: 'Page7.png',
    title: 'Neue Maßnahme',
    caption:
      'Zunächst werden Methode, Name und optional eine Gruppe der Maßnahme festgelegt.',
  },
  {
    src: 'Page8.png',
    title: 'Maßnahmeneingabe',
    caption:
      'Referenz- und geplante Anlage werden erfasst; die kontextbezogene Hilfe unterstützt bei den technischen Parametern.',
  },
  {
    src: 'Page9.png',
    title: 'Analyse',
    caption:
      'Ergebnisse lassen sich prüfen und als Gesamtbericht, Kurzbericht oder Excel-Tabelle herunterladen.',
  },
  {
    src: 'FeinAnalyseExample.png',
    title: 'Word-Bericht',
    caption:
      'Je nach verfügbaren wirtschaftlichen Angaben entsteht eine Potenzialanalyse oder ein detaillierter Einsparpotentialbericht.',
  },
  {
    src: 'KurzreportActionExample.png',
    title: 'Kurzbericht einer Maßnahme',
    caption:
      'Ein kompaktes Dokument mit den wichtigsten technischen, ökologischen und wirtschaftlichen Kennzahlen.',
  },
  {
    src: 'KurzreportGroupExample.png',
    title: 'Kurzbericht einer Gruppe',
    caption:
      'Ein kompaktes Dokument mit den wichtigsten ökologischen und wirtschaftlichen Kennzahlen der Gruppe.',
  },
]

const resolvedImages = computed(() => {
  return images.map(image => {
    const modulePath =
      `../assets/examples/${image.src}`

    return {
      ...image,
      url: exampleImageModules[modulePath] || '',
    }
  })
})

const features = [
  {
    icon: 'mdi-form-textbox',
    title: 'Einfache Dateneingabe',
    description:
      'Projekte, Anlagen und Maßnahmen lassen sich strukturiert und verständlich erfassen.',
  },
  {
    icon: 'mdi-flask-outline',
    title: 'Fundierte Methoden',
    description:
      'Berechnungen basieren auf wissenschaftlichen und zertifizierten Verfahren.',
  },
  {
    icon: 'mdi-folder-multiple-outline',
    title: 'Mehrere Projekte',
    description:
      'Verschiedene Projekte, Systeme und technische Varianten können zentral verwaltet werden.',
  },
  {
    icon: 'mdi-file-export-outline',
    title: 'Berichte und Exporte',
    description:
      'Ergebnisse können als individuelle Berichte und weiterverwendbare Daten exportiert werden.',
  },
]

const viewerOptions = {
  title: false,
  navbar: false,
  toolbar: {
    zoomIn: true,
    zoomOut: true,
    oneToOne: true,
    reset: true,
    prev: false,
    play: false,
    next: false,
    rotateLeft: false,
    rotateRight: false,
    flipHorizontal: false,
    flipVertical: false,
  },
  movable: true,
  zoomable: true,
}
</script>

<style scoped lang="scss">
.about-page {
  max-width: 1440px;
  margin: 0 auto;

  color: rgb(var(--v-theme-on-surface));
}

/* ========================================
   Hero
======================================== */

.about-hero {
  overflow: hidden;

  margin-bottom: 1.5rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 30px;

  background:
    radial-gradient(
      circle at 15% 25%,
      rgba(var(--v-theme-primary), 0.14),
      transparent 44%
    ),
    linear-gradient(
      135deg,
      rgba(var(--v-theme-primary), 0.075),
      rgba(var(--v-theme-primary), 0.015)
    ),
    rgb(var(--v-theme-surface));
}

.hero-content {
  display: grid;
  grid-template-columns: minmax(280px, 0.9fr) minmax(320px, 1.1fr);
  align-items: center;
  gap: 2rem;

  min-height: 420px;
  padding: 2.5rem;
}

.hero-logo {
  display: block;

  width: 100%;
  max-width: 520px;
  height: auto;
  min-width: 0;
  margin: 0 auto;

  object-fit: contain;
}


.hero-copy {
  max-width: 620px;
}

.hero-title {
  margin: 0;

  color: rgb(var(--v-theme-on-surface));

  font-size: clamp(2rem, 4.5vw, 3.6rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.035em;
}

.hero-description {
  max-width: 580px;
  margin: 1rem 0 0;

  color: rgba(var(--v-theme-on-surface), 0.72);

  font-size: 1.05rem;
  line-height: 1.65;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  margin-top: 1.5rem;
}

.hero-actions .v-btn {
  font-weight: 600;
  text-transform: none;
}

/* ========================================
   Main article
======================================== */

.about-content {
  display: grid;
  gap: 1.5rem;
}

.content-section {
  padding: 1.5rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 26px;

  background-color: rgb(var(--v-theme-surface));
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 0.8rem;

  margin-bottom: 1.25rem;
  padding-bottom: 1rem;

  border-bottom: 1px solid
    rgba(var(--v-theme-on-surface), 0.07);
}

.section-heading-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 46px;
  height: 46px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.09);
}

.section-heading h2 {
  margin: 0;

  color: rgb(var(--v-theme-on-surface));

  font-size: 1.35rem;
  font-weight: 750;
  line-height: 1.3;
}

.section-heading p {
  margin: 0.15rem 0 0;

  color: rgba(var(--v-theme-on-surface), 0.62);

  font-size: 0.82rem;
  line-height: 1.4;
}

/* ========================================
   Introduction
======================================== */

.about-introduction {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(320px, 1fr);
  align-items: center;
  gap: 1.5rem;
}

.introduction-copy p,
.benefits-copy p {
  margin: 0 0 1rem;

  color: rgba(var(--v-theme-on-surface), 0.78);

  font-size: 0.96rem;
  line-height: 1.7;
}

.introduction-copy p:last-child,
.benefits-copy p:last-child {
  margin-bottom: 0;
}

.introduction-image {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  min-width: 0;
  height: clamp(280px, 32vw, 420px);
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 20px;

  background-color: rgba(var(--v-theme-primary), 0.025);
}

.introduction-image .feature-image {
  display: block;

  width: 100%;
  height: 100%;
  min-height: 0;

  border: 0;
  border-radius: inherit;

  object-fit: contain;
  object-position: center;

  transition: transform 240ms ease;
}

.introduction-image:hover .feature-image {
  transform: scale(1.02);
}

.industry-image {
  display: block;

  width: 100%;
  min-height: 300px;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 20px;

  background-color: rgba(var(--v-theme-primary), 0.025);

  object-fit: cover;
  object-position: center;
}

/* ========================================
   Feature cards
======================================== */

.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.feature-card {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;

  padding: 1rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 20px;

  background-color: rgba(var(--v-theme-primary), 0.025);

  transition:
    border-color 160ms ease,
    background-color 160ms ease,
    transform 160ms ease;
}

.feature-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.2);

  background-color: rgba(var(--v-theme-primary), 0.055);

  transform: translateY(-2px);
}

.feature-card-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 46px;
  height: 46px;
  border-radius: 16px;

  background-color: rgba(var(--v-theme-primary), 0.09);
}

.feature-card h3 {
  margin: 0;

  color: rgb(var(--v-theme-on-surface));

  font-size: 0.98rem;
  font-weight: 700;
}

.feature-card p {
  margin: 0.3rem 0 0;

  color: rgba(var(--v-theme-on-surface), 0.66);

  font-size: 0.84rem;
  line-height: 1.5;
}

/* ========================================
   Benefits
======================================== */

.benefits-layout {
  display: grid;
  grid-template-columns: minmax(320px, 0.9fr) minmax(320px, 1.1fr);
  align-items: center;
  gap: 1.5rem;
}

/* ========================================
   Program carousel
======================================== */

.program-section {
  overflow: hidden;
}

.program-carousel {
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 22px;

  background-color: rgba(var(--v-theme-primary), 0.02);
}

/* Delimiter dots: dim but visible when inactive, primary + larger when active */
.program-carousel :deep(.v-carousel__controls__item .v-icon) {
  color: rgb(var(--v-theme-on-surface));
  font-size: 11px;
  opacity: 0.32;
}

.program-carousel :deep(.v-carousel__controls__item:hover .v-icon) {
  opacity: 0.65;
}

.program-carousel :deep(.v-carousel__controls__item--active .v-icon) {
  color: rgb(var(--v-theme-primary));
  font-size: 13px;
  opacity: 1;
}

/* Prev/next arrows: solid surface chip so they read on any screenshot */
.program-carousel :deep(.v-window__left),
.program-carousel :deep(.v-window__right) {
  color: rgb(var(--v-theme-primary));

  background-color: rgba(var(--v-theme-surface), 0.92);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 999px;

  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(4px);
}

.program-carousel :deep(.v-window__left):hover,
.program-carousel :deep(.v-window__right):hover {
  background-color: rgb(var(--v-theme-surface));
}

.carousel-slide {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;

  height: 100%;
  /* extra bottom padding reserves room for the carousel delimiter dots
     so they do not overlap the caption */
  padding: 1rem 3.5rem 3.5rem;
}

.carousel-progress {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding-bottom: 0.75rem;

  color: rgba(var(--v-theme-on-surface), 0.62);

  font-size: 0.78rem;
  font-weight: 600;
}

.carousel-image-container {
  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 0;
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 16px;

  background-color: rgba(var(--v-theme-on-surface), 0.025);
}

.carousel-image-container :deep(.carousel-viewer) {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
  min-height: 0;
}

.carousel-image {
  display: block;

  max-width: 100%;
  max-height: 100%;

  object-fit: contain;
  cursor: zoom-in;
}

.carousel-caption {
  padding-top: 1rem;
  text-align: center;
}

.carousel-caption-title {
  color: rgb(var(--v-theme-on-surface));

  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
}

.carousel-caption-text {
  max-width: 560px;
  margin: 0.35rem auto 0;

  color: rgba(var(--v-theme-on-surface), 0.66);

  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.5;
}

/* ========================================
   CTA
======================================== */

.cta-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  padding: 1.25rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.14);
  border-radius: 22px;

  background:
    linear-gradient(
      135deg,
      rgba(var(--v-theme-primary), 0.08),
      rgba(var(--v-theme-primary), 0.025)
    );
}

.cta-panel h3 {
  margin: 0;

  color: rgb(var(--v-theme-on-surface));

  font-size: 1.05rem;
  font-weight: 700;
}

.cta-panel p {
  margin: 0.35rem 0 0;

  color: rgba(var(--v-theme-on-surface), 0.66);

  line-height: 1.5;
}

.cta-actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.cta-actions .v-btn {
  font-weight: 600;
  text-transform: none;
}

/* ========================================
   Development
======================================== */

.development-alert {
  border-radius: 20px;
  line-height: 1.55;
}

.feedback-link {
  color: rgb(var(--v-theme-primary));

  font-weight: 700;
  text-decoration: none;
  text-underline-offset: 3px;
}

.feedback-link:hover {
  text-decoration: underline;
}

/* ========================================
   Responsive
======================================== */

@media (max-width: 960px) {
  .hero-content,
  .about-introduction,
  .benefits-layout {
    grid-template-columns: 1fr;
  }

  .hero-content {
    padding: 2rem;
  }

  .hero-logo {
    max-width: 440px;
  }

  .hero-copy {
    max-width: none;
    text-align: center;
  }

  .hero-description {
    margin-inline: auto;
  }

  .hero-actions {
    justify-content: center;
  }

  .introduction-image {
    height: clamp(260px, 55vw, 400px);
  }

  .industry-image {
    min-height: 260px;
  }

  .cta-panel {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 700px) {
  .about-page {
    padding-inline: 0.5rem;
  }

  .about-hero {
    border-radius: 24px;
  }

  .hero-content {
    gap: 1.25rem;
    min-height: auto;
    padding: 1.5rem 1rem;
  }

  .hero-logo {
    max-width: 320px;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-description {
    font-size: 0.95rem;
  }

  .hero-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .hero-actions .v-btn {
    width: 100%;
  }

  .content-section {
    padding: 1rem;
    border-radius: 22px;
  }

  .section-heading {
    align-items: flex-start;
  }

  .section-heading-icon {
    width: 40px;
    height: 40px;
  }

  .section-heading h2 {
    font-size: 1.15rem;
  }

  .feature-grid {
    grid-template-columns: 1fr;
  }

  .introduction-image {
    height: clamp(220px, 65vw, 320px);
    border-radius: 16px;
  }

  .industry-image {
    min-height: 220px;
  }

  .carousel-slide {
    padding: 0.75rem 2.8rem 3rem;
  }

  .carousel-caption-title {
    font-size: 0.95rem;
  }

  .carousel-caption-text {
    font-size: 0.8rem;
  }

  .cta-actions {
    align-items: stretch;
    flex-direction: column;

    width: 100%;
  }

  .cta-actions .v-btn {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .feature-card,
  .introduction-image .feature-image {
    transition: none;
  }

  .feature-card:hover,
  .introduction-image:hover .feature-image {
    transform: none;
  }
}
</style>
